import { runWithRetry } from "../utils/retry/executor";
import { DEFAULT_GENERATION_RETRY_POLICY, DEFAULT_RETRY_POLICY } from "../types/retry";
import {
  TELEMETRY_CATEGORIES,
  TELEMETRY_EVENTS,
  trackEvent,
} from "../telemetry";

import { buildCorrelationHeaders } from "../telemetry/correlationId";

/** Empty string uses same-origin requests (Webpack dev proxy → FastAPI on :8000). */
const baseUrl = (process.env.REACT_APP_API_BASE_URL ?? "").trim();

export const TOKEN_STORAGE_KEY = "tema_access_token";

export function getStoredAccessToken() {
  try {
    return sessionStorage.getItem(TOKEN_STORAGE_KEY)?.trim() || undefined;
  } catch {
    return undefined;
  }
}

export function setStoredAccessToken(token) {
  sessionStorage.setItem(TOKEN_STORAGE_KEY, token);
}

export function clearStoredAccessToken() {
  sessionStorage.removeItem(TOKEN_STORAGE_KEY);
}

export function buildAuthHeaders(extra = {}) {
  const headers = {
    Accept: "application/json",
    ...buildCorrelationHeaders(),
    ...extra,
  };
  const token = getStoredAccessToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
}

import { parseValidationDetail } from "../validation/apiErrors";

export class ApiError extends Error {
  constructor(message, status, validationDetail = null) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.validationDetail = validationDetail;
    this.validationErrors = validationDetail?.errors ?? [];
  }
}

function formatStructuredValidationDetail(detail) {
  const parsed = parseValidationDetail(detail);
  if (!parsed) {
    return detail?.message ?? null;
  }

  const messages = parsed.errors
    .map((item) => {
      const field = item.field;
      const msg = item.message ?? item.code ?? "Invalid value";
      return field ? `${field}: ${msg}` : msg;
    })
    .filter(Boolean);

  return messages.length > 0 ? messages.join("; ") : parsed.message ?? null;
}

function formatApiErrorDetail(detail) {
  if (typeof detail === "string") {
    return detail;
  }

  if (detail && typeof detail === "object" && !Array.isArray(detail)) {
    const structured = formatStructuredValidationDetail(detail);
    if (structured) return structured;
  }

  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) => {
        if (typeof item === "string") {
          return item;
        }
        if (item && typeof item === "object") {
          const err = item;
          const field = err.loc?.slice(-1)[0];
          const msg = err.msg ?? "Invalid value";
          return field ? `${String(field)}: ${msg}` : msg;
        }
        return null;
      })
      .filter((item) => Boolean(item));

    return messages.length > 0 ? messages.join("; ") : null;
  }

  if (detail && typeof detail === "object") {
    try {
      return JSON.stringify(detail);
    } catch {
      return null;
    }
  }

  return null;
}

function resolveRequestOptions(options) {
  if (options && typeof options === "object") {
    return options;
  }
  return {};
}

async function parseErrorResponse(res, path) {
  let message = res.statusText || "Request failed";
  let validationDetail = null;
  try {
    const body = await res.json();
    validationDetail = parseValidationDetail(body.detail);
    const formatted =
      formatStructuredValidationDetail(body.detail) ?? formatApiErrorDetail(body.detail);
    if (formatted) message = formatted;
  } catch {
    // ignore non-JSON error bodies
  }

  trackEvent(
    TELEMETRY_EVENTS.API_REQUEST_FAILED,
    {
      path,
      status: res.status,
      message,
    },
    {
      category: TELEMETRY_CATEGORIES.API,
      dedupeKey: `${path}:${res.status}:${message}`,
    },
  );

  throw new ApiError(message, res.status, validationDetail);
}

async function apiGetOnce(path, options = {}) {
  const url = `${baseUrl.replace(/\/$/, "")}${path}`;
  console.log(`url: ${url}`);
  const res = await fetch(url, {
    headers: buildAuthHeaders(),
    signal: options.signal,
  });

  if (!res.ok) {
    await parseErrorResponse(res, path);
  }

  return res.json();
}

async function apiPostOnce(path, body, signal) {
  const url = `${baseUrl.replace(/\/$/, "")}${path}`;
  const res = await fetch(url, {
    method: "POST",
    headers: buildAuthHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(body),
    signal,
  });

  if (!res.ok) {
    await parseErrorResponse(res, path);
  }

  return res.json();
}

async function apiPatchOnce(path, body, signal) {
  const url = `${baseUrl.replace(/\/$/, "")}${path}`;
  const res = await fetch(url, {
    method: "PATCH",
    headers: buildAuthHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(body),
    signal,
  });

  if (!res.ok) {
    await parseErrorResponse(res, path);
  }

  return res.json();
}

async function executeWithRetry(operation, path, options, basePolicy) {
  const opts = resolveRequestOptions(options);
  if (opts.retry === false) {
    return operation();
  }

  const { result } = await runWithRetry(operation, {
    policy: opts.retryPolicy ?? basePolicy,
    basePolicy,
    phase: opts.phase ?? "api",
    operationId: path,
    signal: opts.signal,
    context: { domain: "api" },
    shouldRetry: opts.shouldRetry,
    onRetry: opts.onRetry,
  });

  return result;
}

export async function apiGet(path, options = {}) {
  const opts = resolveRequestOptions(options);
  return executeWithRetry(() => apiGetOnce(path, opts), path, opts, DEFAULT_RETRY_POLICY);
}

export async function apiPost(path, body, signal, options = {}) {
  const opts = resolveRequestOptions(options);
  const merged = { ...opts, signal: opts.signal ?? signal };
  return executeWithRetry(() => apiPostOnce(path, body, merged.signal), path, merged, DEFAULT_RETRY_POLICY);
}

export async function apiPatch(path, body, signal, options = {}) {
  const opts = resolveRequestOptions(options);
  const merged = { ...opts, signal: opts.signal ?? signal };
  return executeWithRetry(() => apiPatchOnce(path, body, merged.signal), path, merged, DEFAULT_RETRY_POLICY);
}

export async function apiPostWithGenerationRetry(path, body, signal, options = {}) {
  const opts = resolveRequestOptions(options);
  const merged = { ...opts, signal: opts.signal ?? signal, phase: "generation" };
  return executeWithRetry(
    () => apiPostOnce(path, body, merged.signal),
    path,
    merged,
    DEFAULT_GENERATION_RETRY_POLICY
  );
}
